import { differenceInCalendarDays, startOfDay } from "date-fns";

import {
    UseWorkspaceActivityQueryResultData,
    useWorkspaceActivityQuery,
} from "./useWorkspaceActivityQuery";

const DAYS_CUT_OFF = 3;

type Activity = { lastOnlineDaysAgo: number; numberOfUsers: number };

export type WorkspaceActivity =
    | {
          activity: Activity | null;
          isAvailable: true;
      }
    | {
          isAvailable: false;
      };

export const useWorkspaceActivity = ({
    workspaceId,
    currentWorkspaceId,
}: {
    workspaceId: number;
    currentWorkspaceId: number;
}) => {
    const query = useWorkspaceActivityQuery({
        currentWorkspaceId,
        workspaceIds: [workspaceId],
    })[0];

    if (query.status !== "success") {
        return { status: query.status };
    }

    return {
        status: query.status,
        data: getActivity(query.data),
    };
};

function getActivity({
    isOpenToReplies,
    practiceUserOnlineStatus,
}: UseWorkspaceActivityQueryResultData): WorkspaceActivity {
    if (!isOpenToReplies) {
        return {
            isAvailable: false,
        };
    }

    const days = practiceUserOnlineStatus
        .map(({ lastOnlineAt }) =>
            differenceInCalendarDays(
                startOfDay(new Date()),
                startOfDay(new Date(lastOnlineAt)),
            ),
        )
        .filter((daysAgo) => daysAgo <= DAYS_CUT_OFF);

    if (!days.length) {
        // No one was online in the last 3 days
        return { isAvailable: true, activity: null };
    }

    const mostRecentDay = Math.min(...days);

    return {
        isAvailable: true,
        activity: {
            lastOnlineDaysAgo: mostRecentDay,
            numberOfUsers: days.filter((day) => day === mostRecentDay).length,
        },
    };
}
